// eslint-disable-next-line import/no-cycle
import {
  Property,
  StringMap,
} from '@/types';
// eslint-disable-next-line import/no-cycle
import {
  addSmartListOriginalCode,
  getSmartListHtmlELementFromState,
  TemplateEditorState,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { Maybe } from '@/types/generated-types/graphql';
// eslint-disable-next-line import/no-cycle
import {
  sanitizeTranslationMarkup,
  setTranslationInState,
  generateTranslations,
} from '@/components/template-builder/utils/translate';
import { i18n } from '@/i18n';
// eslint-disable-next-line import/no-cycle
import { TRANSLATION_MARKUP } from '@/components/template-builder/utils/constants';

// eslint-disable-next-line import/no-cycle
import {
  setHtmlContent,
} from '@/components/template-builder/setters';

// eslint-disable-next-line import/no-cycle
import { isEmailTemplate } from '../utils/helpers';

let translator: any;
(async () => {
  translator = await i18n;
})();

/**
 * Strip everything before the widget ID selector so it works in a detached container.
 * E.g., "body #spm_body #spm_widget_xxx .foo" → "#spm_widget_xxx .foo"
 */
const stripSelectorPrefix = (selector: string, widgetId: string): string => {
  const idSelector = `#${widgetId}`;
  const idx = selector.indexOf(idSelector);
  if (idx > 0) {
    return selector.slice(idx);
  }
  return selector;
};

/**
 * Create a detached DOM container from state HTML for safe parsing/modification.
 * Returns { wrapper, widget } — use wrapper for querySelector (full selectors with #widgetId work),
 * use widget.innerHTML to read the modified state HTML back.
 */
const createDetachedContainer = (stateHtml: string, widgetId: string): { wrapper: HTMLElement; widget: HTMLElement } => {
  const wrapper = document.createElement('div');
  let widget: HTMLElement;
  if (isEmailTemplate(TemplateEditorState.template?.type)) {
    widget = document.createElement('table');
  } else {
    widget = document.createElement('div');
  }
  widget.id = widgetId;
  widget.innerHTML = stateHtml;
  wrapper.appendChild(widget);
  return { wrapper, widget };
};

/**
 * Set specific smart list property from original HTML code
 * @param selector
 * @param property
 * @param widgetId
 */
export const setSmartListProperty = (selector: string, property: Property, widgetId: string) => {
  let element: Maybe<string> = getSmartListHtmlELementFromState(widgetId);

  if (element) {
    const pattern = new RegExp(`s-${property.name}="([^"]*)"`, 'is');

    if (pattern.test(element)) {
      element = element.replace(pattern, `s-${property.name}="${property.value}"`);
    } else {
      // We add the property if it doesn't exist
      element = element.replace('s-engine=', `s-${property.name}="${property.value}" s-engine=`);
    }

    addSmartListOriginalCode(widgetId, element);
  }
};

export const setSmartListHtmlCode = (selector: string, property: Property, widgetId: string) => {
  setHtmlContent(selector, property, widgetId);
};

// Layouts for email V3 and below
const layoutsV3: StringMap = {
  horizontal_1: `<tbody>
  <tr>
    <th>
      <table class="row spm_title_wrapper spm_product_list_title_wrapper spm_responsive">
        <tbody>
          <tr>
            <th><h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1></th>
          </tr>
        </tbody>
      </table>
      <table class="row spm_product_list spm_product_list_horizontal_1">
        <tr>
          <td>
            <table class="columns spm_product_list_element_container">
              <tr class="spm_product_list_element_row">
                <th class="column spm_product_list_element" style="width: 100%;"
                s-collection="products" s-item="product" s-grid="1" s-nb="4" s-engine="crossselling"
                s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
                  <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="50"}</a></h4>
                  <p class="spm_product_list_element_image_wrapper" style="text-align: center; margin: 0; padding: 0;">
                    <a href="{var=product.url}">
                      <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
                      width="200" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
                    </a>
                  </p>
                  <table class="spm_product_list_element_description spm_hide_on_builder" style="height: 70px;"
                  height="70" cellpadding="0" cellspacing="0" border="0" width="100%">
                    <tr>
                      <td><p>{var=product.description_short truncate="100"}</p></td>
                    </tr>
                  </table>
                  <table class="row">
                    <tr>
                      <td>
                        <table class="columns spm_product_list_element_prices">
                          <tr>
                            <th s-if="product.price_strike" s-condition="!=" s-value="product.price"
                            class="column" align="center" valign="middle"><p class="spm_product_list_element_price_strike">{var=product.price_strike}</p></th>
                            <th class="column" align="center" valign="middle"><p class="spm_product_list_element_price">{var=product.price}</p></th>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                  <table class="small-button button spm_product_list_element_button spm_display_fullwidth_mobile">
                    <tr>
                      <th><a href="{var=product.url}" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a></th>
                    </tr>
                  </table>
                </th>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</tbody>`,

  horizontal_2: `<tbody>
  <tr>
    <th>
      <table class="row spm_title_wrapper spm_product_list_title_wrapper spm_responsive">
        <tbody>
          <tr>
            <th><h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1></th>
          </tr>
        </tbody>
      </table>
      <table class="row spm_product_list spm_product_list_horizontal_2">
        <tr>
          <td>
            <table class="columns spm_product_list_element_container">
              <tr class="spm_product_list_element_row">
                <th class="column spm_product_list_element" style="width: 50%;"
                s-collection="products" s-item="product" s-grid="2" s-nb="4" s-engine="crossselling"
                s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
                  <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="40"}</a></h4>
                  <p class="spm_product_list_element_image_wrapper" style="text-align:center;margin:0;padding:0;">
                    <a href="{var=product.url}">
                      <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}" width="200"
                      class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
                    </a>
                  </p>
                  <table class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;" height="70" cellpadding="0" cellspacing="0" border="0" width="100%">
                    <tr>
                      <td>
                        <p>{var=product.description_short truncate="100"}</p>
                      </td>
                    </tr>
                  </table>
                  <table class="row">
                    <tr>
                      <td>
                        <table class="columns spm_product_list_element_prices">
                          <tr>
                            <th s-if="product.price_strike" s-condition="!=" s-value="product.price"
                            class="column" align="center" valign="middle"><p class="spm_product_list_element_price_strike">{var=product.price_strike}</p></th>
                            <th class="column" align="center" valign="middle"><p class="spm_product_list_element_price">{var=product.price}</p></th>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                  <table class="small-button button spm_product_list_element_button spm_display_fullwidth_mobile">
                    <tr>
                      <th><a href="{var=product.url}" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a></th>
                    </tr>
                  </table>
                </th>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</tbody>`,

  horizontal_3: `<tbody>
  <tr>
    <th>
      <table class="row spm_title_wrapper spm_product_list_title_wrapper spm_responsive">
        <tbody>
          <tr>
            <th><h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1></th>
          </tr>
        </tbody>
      </table>
      <table class="row spm_product_list spm_product_list_horizontal_3">
        <tr>
          <td>
            <table class="columns spm_product_list_element_container">
              <tr class="spm_product_list_element_row">
                  <th class="column spm_product_list_element" style="width: 33.33333%;"
                  s-collection="products" s-item="product" s-grid="3" s-nb="3" s-engine="crossselling"
                  s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
                  <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="40"}</a></h4>
                    <p class="spm_product_list_element_image_wrapper" style="text-align:center;margin:0;padding:0;">
                      <a href="{var=product.url}">
                        <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
                        width="150" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
                      </a>
                    </p>
                    <table class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;"
                    height="70" cellpadding="0" cellspacing="0" border="0" width="100%">
                        <tr>
                          <td><p>{var=product.description_short truncate="80"}</p></td>
                        </tr>
                    </table>
                    <table class="row">
                        <tr>
                          <td>
                            <table class="columns spm_product_list_element_prices">
                              <tr>
                                <th s-if="product.price_strike" s-condition="!=" s-value="product.price" class="column" align="center" valign="middle">
                                  <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
                                </th>
                                <th class="column" align="center" valign="middle">
                                  <p class="spm_product_list_element_price">{var=product.price}</p>
                                </th>
                              </tr>
                            </table>
                          </td>
                        </tr>
                    </table>
                    <table class="small-button button spm_product_list_element_button spm_display_fullwidth_mobile">
                      <tr>
                        <th><a href="{var=product.url}" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a></th>
                      </tr>
                    </table>
                  </th>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</tbody>`,

  horizontal_4: `<tbody>
  <tr>
    <th>
      <table class="row spm_title_wrapper spm_product_list_title_wrapper spm_responsive">
        <tbody>
          <tr>
            <th><h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1></th>
          </tr>
        </tbody>
      </table>
      <table class="row spm_product_list spm_product_list_horizontal_4">
        <tr>
          <td>
            <table class="columns spm_product_list_element_container">
              <tr class="spm_product_list_element_row">
                <th class="column spm_product_list_element" style="width: 25%;"
                s-collection="products" s-item="product" s-grid="4" s-nb="4" s-engine="crossselling"
                s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
                  <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="20"}</a></h4>
                  <p class="spm_product_list_element_image_wrapper" style="text-align:center;margin:0;padding:0;">
                    <a href="{var=product.url}">
                      <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
                      width="100" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
                    </a>
                  </p>
                  <table class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;" height="70" cellpadding="0" cellspacing="0" border="0" width="100%">
                    <tr>
                      <td>
                          <p>{var=product.description_short truncate="60"}</p>
                      </td>
                    </tr>
                  </table>
                  <table class="row ">
                    <tr>
                      <td>
                        <table class="columns spm_product_list_element_prices">
                          <tr>
                              <th s-if="product.price_strike" s-condition="!=" s-value="product.price" class="column" align="center" valign="middle">
                                <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
                              </th>
                              <th class="column" align="center" valign="middle">
                                <p class="spm_product_list_element_price">{var=product.price}</p>
                              </th>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                  <table class="small-button button spm_product_list_element_button spm_display_fullwidth_mobile">
                    <tr>
                      <th><a href="{var=product.url}" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a></th>
                    </tr>
                  </table>
                </th>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</tbody>`,

  vertical_1: `<tbody>
  <tr>
    <th>
      <table class="row spm_title_wrapper spm_product_list_title_wrapper spm_responsive">
        <tbody>
          <tr>
            <th><h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1></th>
          </tr>
        </tbody>
      </table>
      <table class="row spm_product_list spm_product_list_vertical_1">
        <tr>
          <td>
            <table class="columns spm_product_list_element_container">
              <tr class="spm_product_list_element_row">
                <th class="column spm_product_list_element" style="width: 100%;"
                s-collection="products" s-item="product" s-grid="1" s-nb="4" s-engine="crossselling"
                s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
                  <table width="100%" style="width: 100%">
                    <tr>
                      <th class="column" style="width: 30%;">
                        <a href="{var=product.url}">
                          <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
                          width="150" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
                        </a>
                      </th>
                      <th class="column" style="padding: 5px; width: 70%;">
                        <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="50"}</a></h4>
                        <table class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;" height="70"
                          cellpadding="0" cellspacing="0" border="0" width="100%">
                          <tr>
                            <td>
                              <p>{var=product.description_short truncate="100"}</p>
                            </td>
                          </tr>
                        </table>
                        <table class="columns spm_product_list_element_prices">
                          <tr>
                            <th s-if="product.price_strike" s-condition="!=" s-value="product.price" class="column" align="center" valign="middle">
                              <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
                            </th>
                            <th class="column" align="center" valign="middle">
                              <p class="spm_product_list_element_price">{var=product.price}</p>
                            </th>
                          </tr>
                        </table>
                        <table class="small-button button spm_product_list_element_button spm_display_fullwidth_mobile">
                          <tr>
                            <th><a href="{var=product.url}" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a></th>
                          </tr>
                        </table>
                      </th>
                    </tr>
                  </table>
                </th>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</tbody>`,
};

// Layouts for email V4 (same structure as V3, separated for independent evolution)
const layoutsV4: StringMap = {
  horizontal_1: `<tbody>
  <tr>
    <th>
      <table class="row spm_title_wrapper spm_product_list_title_wrapper spm_responsive">
        <tbody>
          <tr>
            <th><h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1></th>
          </tr>
        </tbody>
      </table>
      <table class="row spm_product_list spm_product_list_horizontal_1">
        <tr>
          <td>
            <table class="columns spm_product_list_element_container">
              <tr class="spm_product_list_element_row">
                <th class="column spm_product_list_element" style="width: 100%;"
                s-collection="products" s-item="product" s-grid="1" s-nb="4" s-engine="crossselling"
                s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
                  <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="50"}</a></h4>
                  <p class="spm_product_list_element_image_wrapper" style="text-align: center; margin: 0; padding: 0;">
                    <a href="{var=product.url}">
                      <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
                      width="200" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
                    </a>
                  </p>
                  <table class="spm_product_list_element_description spm_hide_on_builder" style="height: 70px;"
                  height="70" cellpadding="0" cellspacing="0" border="0" width="100%">
                    <tr>
                      <td><p>{var=product.description_short truncate="100"}</p></td>
                    </tr>
                  </table>
                  <table class="row">
                    <tr>
                      <td>
                        <table class="columns spm_product_list_element_prices">
                          <tr>
                            <th s-if="product.price_strike" s-condition="!=" s-value="product.price"
                            class="column" align="center" valign="middle"><p class="spm_product_list_element_price_strike">{var=product.price_strike}</p></th>
                            <th class="column" align="center" valign="middle"><p class="spm_product_list_element_price">{var=product.price}</p></th>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                  <table class="spm_button spm_product_list_element_button spm_display_fullwidth_mobile" width="100%" cellspacing="0" cellpadding="0">
                    <tr>
                      <th><a href="{var=product.url}" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a></th>
                    </tr>
                  </table>
                </th>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</tbody>`,

  horizontal_2: `<tbody>
  <tr>
    <th>
      <table class="row spm_title_wrapper spm_product_list_title_wrapper spm_responsive">
        <tbody>
          <tr>
            <th><h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1></th>
          </tr>
        </tbody>
      </table>
      <table class="row spm_product_list spm_product_list_horizontal_2">
        <tr>
          <td>
            <table class="columns spm_product_list_element_container">
              <tr class="spm_product_list_element_row">
                <th class="column spm_product_list_element" style="width: 50%;"
                s-collection="products" s-item="product" s-grid="2" s-nb="4" s-engine="crossselling"
                s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
                  <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="40"}</a></h4>
                  <p class="spm_product_list_element_image_wrapper" style="text-align:center;margin:0;padding:0;">
                    <a href="{var=product.url}">
                      <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}" width="200"
                      class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
                    </a>
                  </p>
                  <table class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;" height="70" cellpadding="0" cellspacing="0" border="0" width="100%">
                    <tr>
                      <td>
                        <p>{var=product.description_short truncate="100"}</p>
                      </td>
                    </tr>
                  </table>
                  <table class="row">
                    <tr>
                      <td>
                        <table class="columns spm_product_list_element_prices">
                          <tr>
                            <th s-if="product.price_strike" s-condition="!=" s-value="product.price"
                            class="column" align="center" valign="middle"><p class="spm_product_list_element_price_strike">{var=product.price_strike}</p></th>
                            <th class="column" align="center" valign="middle"><p class="spm_product_list_element_price">{var=product.price}</p></th>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                  <table class="spm_button spm_product_list_element_button spm_display_fullwidth_mobile" width="100%" cellspacing="0" cellpadding="0">
                    <tr>
                      <th><a href="{var=product.url}" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a></th>
                    </tr>
                  </table>
                </th>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</tbody>`,

  horizontal_3: `<tbody>
  <tr>
    <th>
      <table class="row spm_title_wrapper spm_product_list_title_wrapper spm_responsive">
        <tbody>
          <tr>
            <th><h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1></th>
          </tr>
        </tbody>
      </table>
      <table class="row spm_product_list spm_product_list_horizontal_3">
        <tr>
          <td>
            <table class="columns spm_product_list_element_container">
              <tr class="spm_product_list_element_row">
                  <th class="column spm_product_list_element" style="width: 33.33333%;"
                  s-collection="products" s-item="product" s-grid="3" s-nb="3" s-engine="crossselling"
                  s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
                  <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="40"}</a></h4>
                    <p class="spm_product_list_element_image_wrapper" style="text-align:center;margin:0;padding:0;">
                      <a href="{var=product.url}">
                        <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
                        width="150" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
                      </a>
                    </p>
                    <table class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;"
                    height="70" cellpadding="0" cellspacing="0" border="0" width="100%">
                        <tr>
                          <td><p>{var=product.description_short truncate="80"}</p></td>
                        </tr>
                    </table>
                    <table class="row">
                        <tr>
                          <td>
                            <table class="columns spm_product_list_element_prices">
                              <tr>
                                <th s-if="product.price_strike" s-condition="!=" s-value="product.price" class="column" align="center" valign="middle">
                                  <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
                                </th>
                                <th class="column" align="center" valign="middle">
                                  <p class="spm_product_list_element_price">{var=product.price}</p>
                                </th>
                              </tr>
                            </table>
                          </td>
                        </tr>
                    </table>
                    <table class="spm_button spm_product_list_element_button spm_display_fullwidth_mobile" width="100%" cellspacing="0" cellpadding="0">
                      <tr>
                        <th><a href="{var=product.url}" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a></th>
                      </tr>
                    </table>
                  </th>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</tbody>`,

  horizontal_4: `<tbody>
  <tr>
    <th>
      <table class="row spm_title_wrapper spm_product_list_title_wrapper spm_responsive">
        <tbody>
          <tr>
            <th><h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1></th>
          </tr>
        </tbody>
      </table>
      <table class="row spm_product_list spm_product_list_horizontal_4">
        <tr>
          <td>
            <table class="columns spm_product_list_element_container">
              <tr class="spm_product_list_element_row">
                <th class="column spm_product_list_element" style="width: 25%;"
                s-collection="products" s-item="product" s-grid="4" s-nb="4" s-engine="crossselling"
                s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
                  <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="30"}</a></h4>
                  <p class="spm_product_list_element_image_wrapper" style="text-align:center;margin:0;padding:0;">
                    <a href="{var=product.url}">
                      <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}" width="150"
                      class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
                    </a>
                  </p>
                  <table class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;"
                  height="70" cellpadding="0" cellspacing="0" border="0" width="100%">
                    <tr>
                      <td><p>{var=product.description_short truncate="60"}</p></td>
                    </tr>
                  </table>
                  <table class="row">
                    <tr>
                      <td>
                        <table class="columns spm_product_list_element_prices">
                          <tr>
                            <th s-if="product.price_strike" s-condition="!=" s-value="product.price" class="column" align="center" valign="middle">
                              <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
                            </th>
                            <th class="column" align="center" valign="middle">
                              <p class="spm_product_list_element_price">{var=product.price}</p>
                            </th>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                  <table class="spm_button spm_product_list_element_button spm_display_fullwidth_mobile" width="100%" cellspacing="0" cellpadding="0">
                    <tr>
                      <th><a href="{var=product.url}" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a></th>
                    </tr>
                  </table>
                </th>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</tbody>`,

  vertical_1: `<tbody>
  <tr>
    <th>
      <table class="row spm_title_wrapper spm_product_list_title_wrapper spm_responsive">
        <tbody>
          <tr>
            <th><h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1></th>
          </tr>
        </tbody>
      </table>
      <table class="row spm_product_list spm_product_list_vertical_1">
        <tr>
          <td>
            <table class="columns spm_product_list_element_container">
              <tr class="spm_product_list_element_row">
                <th class="column spm_product_list_element" style="width: 100%;"
                s-collection="products" s-item="product" s-grid="1" s-nb="4" s-engine="crossselling"
                s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
                  <table width="100%" style="width: 100%">
                    <tr>
                      <th class="column" style="width: 30%;">
                        <a href="{var=product.url}">
                          <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
                          width="150" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
                        </a>
                      </th>
                      <th class="column" style="padding: 5px; width: 70%;">
                        <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="50"}</a></h4>
                        <table class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;" height="70"
                          cellpadding="0" cellspacing="0" border="0" width="100%">
                          <tr>
                            <td>
                              <p>{var=product.description_short truncate="100"}</p>
                            </td>
                          </tr>
                        </table>
                        <table class="columns spm_product_list_element_prices">
                          <tr>
                            <th s-if="product.price_strike" s-condition="!=" s-value="product.price" class="column" align="center" valign="middle">
                              <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
                            </th>
                            <th class="column" align="center" valign="middle">
                              <p class="spm_product_list_element_price">{var=product.price}</p>
                            </th>
                          </tr>
                        </table>
                        <table class="spm_button spm_product_list_element_button spm_display_fullwidth_mobile" width="100%" cellspacing="0" cellpadding="0">
                          <tr>
                            <th><a href="{var=product.url}" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a></th>
                          </tr>
                        </table>
                      </th>
                    </tr>
                  </table>
                </th>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </th>
  </tr>
</tbody>`,
};

export const setListLayout = (selector: string, property: Property, widgetId: string) => {
  const previousLayout: Maybe<string> = getSmartListHtmlELementFromState(widgetId);
  if (!previousLayout) return;

  if (layoutsV3[property.value]) {
    let layout = layoutsV3[property.value];

    // Parse previous layout in detached container to extract button text and title
    const { wrapper: detached } = createDetachedContainer(previousLayout, widgetId);

    // keep button text
    const button = detached.querySelector('.spm_product_list_element_button > tbody > tr > th > a');
    if (button) {
      const buttonText = button.getAttribute('title');

      if (buttonText) {
        layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL}', buttonText);
      } else {
        layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL}', translator.global.t('templateBuilder.smartProductListDefaultButton'));
      }

      const translationValue = button.querySelector(TRANSLATION_MARKUP);

      if (!translationValue) {
        const { newHtml } = generateTranslations(translator.global.t('templateBuilder.smartProductListDefaultButton'), { newElement: true });
        button.innerHTML = `${newHtml}`;
      } else if (translationValue && translationValue.innerHTML === '') {
        translationValue.innerHTML = translator.global.t('templateBuilder.smartProductListDefaultButton');
      }

      layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL_TRANSLATION}', button.innerHTML);
    } else {
      layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL}', translator.global.t('templateBuilder.smartProductListDefaultButton'));
      const { newHtml } = generateTranslations(translator.global.t('templateBuilder.smartProductListDefaultButton'));
      layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL_TRANSLATION}', `${newHtml}`);
    }

    const listTitle = detached.querySelector('.spm_product_list_title');

    if (listTitle) {
      const titleTranslationValue = listTitle.querySelector(TRANSLATION_MARKUP);

      if (titleTranslationValue && titleTranslationValue.innerHTML === '') {
        titleTranslationValue.innerHTML = translator.global.t('templateBuilder.smartProductListDefaultTitle');
      }

      layout = layout.replace('{PRODUCT_LIST_TITLE}', listTitle.innerHTML);
    } else {
      const { newHtml } = generateTranslations(translator.global.t('templateBuilder.smartProductListDefaultTitle'));
      layout = layout.replace('{PRODUCT_LIST_TITLE}', `${newHtml}`);
    }

    const attributesToKeep = ['engine', 'ids', ' excluded_ids', ' excluded_categories', ' excluded_manufacturers', ' nb_weeks'];
    attributesToKeep.forEach((value) => {
      const pattern = new RegExp(`s-${value}="([^"]*)"`, 'is');
      const find = previousLayout?.match(pattern);
      if (find) {
        layout = layout.replace(pattern, `s-${value}="${find[1]}"`);
      }
    });
    addSmartListOriginalCode(widgetId, layout);
  }
  if (previousLayout && property.value === 'custom') {
    let layout = previousLayout;
    layout = layout.replace(/spm_product_list_(horizontal|vertical)_\d/g, 'spm_product_list_custom');
    addSmartListOriginalCode(widgetId, layout);
  }
};

// V4 version of setListLayout (same logic, uses layoutsV4)
export const setListLayoutV4 = (selector: string, property: Property, widgetId: string) => {
  const previousLayout: Maybe<string> = getSmartListHtmlELementFromState(widgetId);
  if (!previousLayout) return;

  if (layoutsV4[property.value]) {
    let layout = layoutsV4[property.value];

    // Parse previous layout in detached container to extract button text and title
    const { wrapper: detached } = createDetachedContainer(previousLayout, widgetId);

    const button = detached.querySelector('.spm_product_list_element_button > tbody > tr > th > a');
    if (button) {
      const buttonText = button.getAttribute('title');

      if (buttonText) {
        layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL}', buttonText);
      } else {
        layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL}', translator.global.t('templateBuilder.smartProductListDefaultButton'));
      }

      const translationValue = button.querySelector(TRANSLATION_MARKUP);

      if (!translationValue) {
        const { newHtml } = generateTranslations(translator.global.t('templateBuilder.smartProductListDefaultButton'), { newElement: true });
        button.innerHTML = `${newHtml}`;
      } else if (translationValue && translationValue.innerHTML === '') {
        translationValue.innerHTML = translator.global.t('templateBuilder.smartProductListDefaultButton');
      }

      layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL_TRANSLATION}', button.innerHTML);
    } else {
      layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL}', translator.global.t('templateBuilder.smartProductListDefaultButton'));
      const { newHtml } = generateTranslations(translator.global.t('templateBuilder.smartProductListDefaultButton'));
      layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL_TRANSLATION}', `${newHtml}`);
    }

    const listTitle = detached.querySelector('.spm_product_list_title');

    if (listTitle) {
      const titleTranslationValue = listTitle.querySelector(TRANSLATION_MARKUP);

      if (titleTranslationValue && titleTranslationValue.innerHTML === '') {
        titleTranslationValue.innerHTML = translator.global.t('templateBuilder.smartProductListDefaultTitle');
      }

      layout = layout.replace('{PRODUCT_LIST_TITLE}', listTitle.innerHTML);
    } else {
      const { newHtml } = generateTranslations(translator.global.t('templateBuilder.smartProductListDefaultTitle'));
      layout = layout.replace('{PRODUCT_LIST_TITLE}', `${newHtml}`);
    }

    const attributesToKeep = ['engine', 'ids', ' excluded_ids', ' excluded_categories', ' excluded_manufacturers', ' nb_weeks'];
    attributesToKeep.forEach((value) => {
      const pattern = new RegExp(`s-${value}="([^"]*)"`, 'is');
      const find = previousLayout?.match(pattern);
      if (find) {
        layout = layout.replace(pattern, `s-${value}="${find[1]}"`);
      }
    });
    addSmartListOriginalCode(widgetId, layout);
  }
  if (previousLayout && property.value === 'custom') {
    let layout = previousLayout;
    layout = layout.replace(/spm_product_list_(horizontal|vertical)_\d/g, 'spm_product_list_custom');
    addSmartListOriginalCode(widgetId, layout);
  }
};

export const setTruncateLimit = (selector: string, property: Property, widgetId: string) => {
  let element: Maybe<string> = getSmartListHtmlELementFromState(widgetId);

  if (element) {
    const regExp = new RegExp(`(\\{var="?${selector}"?[^}]*truncate="?)([0-9]+)("?[^}]*})`, 'gis');
    const regExp2 = new RegExp(`(\\{var="?${selector}"?[^}]*)(})`, 'gis');
    if (!element.match(regExp)) {
      element = element.replace(regExp2, `$1 truncate=${property.value.trim()}$2`);
    } else {
      element = element.replace(regExp, `$1${property.value}$3`);
    }
    addSmartListOriginalCode(widgetId, element);
  }
};

export const setDisplayedElements = (selector: string, property: Property, widgetId: string) => {
  let element: Maybe<string> = getSmartListHtmlELementFromState(widgetId);

  // Elements are hidden by adding "spm_hide_on_builder" class
  const allElements = [
    'spm_product_list_element_button',
    'spm_product_list_element_description',
    'spm_product_list_element_price',
    'spm_product_list_element_price_strike',
    'spm_product_list_element_image',
    'spm_product_list_element_title',
    'spm_product_list_title',
  ];

  if (element) {
    allElements.forEach((displayableElement) => {
      // We check if class exists or not
      const regExp = new RegExp(`"([^"]*\\b${displayableElement}\\b(?:\\s[^"]+)*)"`, 'is');

      if (element) {
        const matches = element.match(regExp);

        if (matches) {
          const [, match] = matches;
          const classList = match.split(' ');
          let newClassList = match;

          if (property.value.includes(displayableElement) && classList.includes('spm_hide_on_builder')) {
            // If we need to display the item, we need to remove the class
            newClassList = classList.filter((className) => className !== 'spm_hide_on_builder').join(' ');
          } else if (!property.value.includes(displayableElement) && !classList.includes('spm_hide_on_builder')) {
            // If we need to hide the item, we need to add the class
            newClassList = [...classList, 'spm_hide_on_builder'].join(' ');
          }

          // We replace the match by the new class list (containing or not the hidden class)
          element = element.replace(`class="${match}"`, `class="${newClassList}"`);
        }
      }
    });

    addSmartListOriginalCode(widgetId, element);
  }
};

export const setSmartListTitle = (selector: string, property: Property, widgetId: string) => {
  const stateHtml = getSmartListHtmlELementFromState(widgetId);
  if (!stateHtml || property.value === undefined) return;

  const { wrapper, widget } = createDetachedContainer(stateHtml, widgetId);
  const element: HTMLElement | null = wrapper.querySelector(stripSelectorPrefix(selector, widgetId));
  if (!element) return;

  sanitizeTranslationMarkup(element).innerHTML = property.value;
  setTranslationInState(element, 'markup');
  addSmartListOriginalCode(widgetId, widget.innerHTML);
};

export const setSmartListButtonTitle = (selector: string, property: Property, widgetId: string) => {
  const stateHtml = getSmartListHtmlELementFromState(widgetId);
  if (!stateHtml || property.value === undefined) return;

  const { wrapper, widget } = createDetachedContainer(stateHtml, widgetId);
  const element: HTMLElement | null = wrapper.querySelector(stripSelectorPrefix(selector, widgetId));
  if (!element) return;

  sanitizeTranslationMarkup(element).innerHTML = property.value;
  setTranslationInState(element, 'markup', '', true);
  addSmartListOriginalCode(widgetId, widget.innerHTML);
};

export const setSmartListButtonAlignment = (selector: string, property: Property, widgetId: string) => {
  const stateHtml = getSmartListHtmlELementFromState(widgetId);
  if (!stateHtml || property.value === undefined) return;

  const { wrapper, widget } = createDetachedContainer(stateHtml, widgetId);
  const element: HTMLElement | null = wrapper.querySelector(stripSelectorPrefix(selector, widgetId));
  if (!element) return;

  element.setAttribute(property.name, property.value);
  addSmartListOriginalCode(widgetId, widget.innerHTML);
};

export const setSmartListImageWidth = (selector: string, property: Property, widgetId: string) => {
  const stateHtml = getSmartListHtmlELementFromState(widgetId);
  if (!stateHtml || property.value === undefined) return;

  const { wrapper, widget } = createDetachedContainer(stateHtml, widgetId);
  const element = wrapper.querySelector(stripSelectorPrefix(selector, widgetId)) as HTMLElement | null;
  if (!element) return;

  if (element.tagName === 'IMG') {
    element.setAttribute('width', String(property.value));
  }
  addSmartListOriginalCode(widgetId, widget.innerHTML);
};

/**
 * Layouts for popup/embed using div structure instead of tables
 */
export const popupEmbedLayouts: StringMap = {
  horizontal_1: `<div class="spm_title_wrapper spm_product_list_title_wrapper">
  <h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1>
</div>
<div class="spm_product_list spm_product_list_horizontal_1">
  <div class="spm_product_list_element_container">
    <div class="spm_product_list_element_row">
      <div class="spm_product_list_element" style="width: 100%;"
      s-collection="products" s-item="product" s-grid="1" s-nb="4" s-engine="crossselling"
      s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
        <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="50"}</a></h4>
        <div class="spm_product_list_element_image_wrapper" style="text-align: center; margin: 0; padding: 0;">
          <a href="{var=product.url}">
            <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
            width="200" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
          </a>
        </div>
        <div class="spm_product_list_element_description spm_hide_on_builder" style="height: 70px;">
          <p>{var=product.description_short truncate="100"}</p>
        </div>
        <div class="spm_product_list_element_prices">
          <div s-if="product.price_strike" s-condition="!=" s-value="product.price" class="spm_product_list_element_price_strike_wrapper" style="text-align: center;">
            <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
          </div>
          <div class="spm_product_list_element_price_wrapper" style="text-align: center;">
            <p class="spm_product_list_element_price">{var=product.price}</p>
          </div>
        </div>
        <div class="spm_product_list_element_button">
          <a href="{var=product.url}" class="spm_button" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a>
        </div>
      </div>
    </div>
  </div>
</div>`,

  horizontal_2: `<div class="spm_title_wrapper spm_product_list_title_wrapper">
  <h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1>
</div>
<div class="spm_product_list spm_product_list_horizontal_2">
  <div class="spm_product_list_element_container">
    <div class="spm_product_list_element_row">
      <div class="spm_product_list_element" style="width: 50%;"
      s-collection="products" s-item="product" s-grid="2" s-nb="4" s-engine="crossselling"
      s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
        <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="40"}</a></h4>
        <div class="spm_product_list_element_image_wrapper" style="text-align:center;margin:0;padding:0;">
          <a href="{var=product.url}">
            <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}" width="200"
            class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
          </a>
        </div>
        <div class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;">
          <p>{var=product.description_short truncate="100"}</p>
        </div>
        <div class="spm_product_list_element_prices">
          <div s-if="product.price_strike" s-condition="!=" s-value="product.price" class="spm_product_list_element_price_strike_wrapper" style="text-align: center;">
            <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
          </div>
          <div class="spm_product_list_element_price_wrapper" style="text-align: center;">
            <p class="spm_product_list_element_price">{var=product.price}</p>
          </div>
        </div>
        <div class="spm_product_list_element_button">
          <a href="{var=product.url}" class="spm_button" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a>
        </div>
      </div>
    </div>
  </div>
</div>`,

  horizontal_3: `<div class="spm_title_wrapper spm_product_list_title_wrapper">
  <h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1>
</div>
<div class="spm_product_list spm_product_list_horizontal_3">
  <div class="spm_product_list_element_container">
    <div class="spm_product_list_element_row">
      <div class="spm_product_list_element" style="width: 33.33333%;"
      s-collection="products" s-item="product" s-grid="3" s-nb="3" s-engine="crossselling"
      s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
        <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="40"}</a></h4>
        <div class="spm_product_list_element_image_wrapper" style="text-align:center;margin:0;padding:0;">
          <a href="{var=product.url}">
            <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
            width="150" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
          </a>
        </div>
        <div class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;">
          <p>{var=product.description_short truncate="80"}</p>
        </div>
        <div class="spm_product_list_element_prices">
          <div s-if="product.price_strike" s-condition="!=" s-value="product.price" class="spm_product_list_element_price_strike_wrapper" style="text-align: center;">
            <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
          </div>
          <div class="spm_product_list_element_price_wrapper" style="text-align: center;">
            <p class="spm_product_list_element_price">{var=product.price}</p>
          </div>
        </div>
        <div class="spm_product_list_element_button">
          <a href="{var=product.url}" class="spm_button" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a>
        </div>
      </div>
    </div>
  </div>
</div>`,

  horizontal_4: `<div class="spm_title_wrapper spm_product_list_title_wrapper">
  <h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1>
</div>
<div class="spm_product_list spm_product_list_horizontal_4">
  <div class="spm_product_list_element_container">
    <div class="spm_product_list_element_row">
      <div class="spm_product_list_element" style="width: 25%;"
      s-collection="products" s-item="product" s-grid="4" s-nb="4" s-engine="crossselling"
      s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
        <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="20"}</a></h4>
        <div class="spm_product_list_element_image_wrapper" style="text-align:center;margin:0;padding:0;">
          <a href="{var=product.url}">
            <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
            width="100" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
          </a>
        </div>
        <div class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;">
          <p>{var=product.description_short truncate="60"}</p>
        </div>
        <div class="spm_product_list_element_prices">
          <div s-if="product.price_strike" s-condition="!=" s-value="product.price" class="spm_product_list_element_price_strike_wrapper" style="text-align: center;">
            <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
          </div>
          <div class="spm_product_list_element_price_wrapper" style="text-align: center;">
            <p class="spm_product_list_element_price">{var=product.price}</p>
          </div>
        </div>
        <div class="spm_product_list_element_button">
          <a href="{var=product.url}" class="spm_button" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a>
        </div>
      </div>
    </div>
  </div>
</div>`,

  vertical_1: `<div class="spm_title_wrapper spm_product_list_title_wrapper">
  <h1 class="spm_product_list_title spm_inline_editing">{PRODUCT_LIST_TITLE}</h1>
</div>
<div class="spm_product_list spm_product_list_vertical_1">
  <div class="spm_product_list_element_container">
    <div class="spm_product_list_element_row">
      <div class="spm_product_list_element" style="width: 100%; display: flex;"
      s-collection="products" s-item="product" s-grid="1" s-nb="4" s-engine="crossselling"
      s-ids="" s-excluded_ids="" s-excluded_categories="" s-excluded_manufacturers="" s-nb_weeks="4">
        <div class="spm_product_list_element_image_col" style="width: 30%;">
          <a href="{var=product.url}">
            <img src="{var=product.image_url resize=400x400 resizeType=fill-to-fit}"
            width="150" class="spm_product_list_element_image" alt="{var=product.name}" title="{var=product.name}" />
          </a>
        </div>
        <div class="spm_product_list_element_content_col" style="padding: 5px; width: 70%;">
          <h4 class="spm_product_list_element_title"><a href="{var=product.url}" title="{var=product.name}">{var=product.name truncate="50"}</a></h4>
          <div class="spm_product_list_element_description spm_hide_on_builder" style="height:70px;">
            <p>{var=product.description_short truncate="100"}</p>
          </div>
          <div class="spm_product_list_element_prices">
            <div s-if="product.price_strike" s-condition="!=" s-value="product.price" class="spm_product_list_element_price_strike_wrapper" style="text-align: center;">
              <p class="spm_product_list_element_price_strike">{var=product.price_strike}</p>
            </div>
            <div class="spm_product_list_element_price_wrapper" style="text-align: center;">
              <p class="spm_product_list_element_price">{var=product.price}</p>
            </div>
          </div>
          <div class="spm_product_list_element_button">
            <a href="{var=product.url}" class="spm_button" title="{PRODUCT_BUTTON_LABEL}">{PRODUCT_BUTTON_LABEL_TRANSLATION}</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>`,
};

/**
 * Set list layout for popup/embed widgets
 */
export const setListLayoutPopupEmbed = (selector: string, property: Property, widgetId: string) => {
  const previousLayout: Maybe<string> = getSmartListHtmlELementFromState(widgetId);
  if (!previousLayout) return;

  if (popupEmbedLayouts[property.value]) {
    let layout = popupEmbedLayouts[property.value];

    // Parse previous layout in detached container to extract button text and title
    const { wrapper: detached } = createDetachedContainer(previousLayout, widgetId);

    const button = detached.querySelector('.spm_product_list_element_button a.spm_button');
    if (button) {
      const buttonText = button.getAttribute('title');

      if (buttonText) {
        layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL}', buttonText);
      } else {
        layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL}', translator.global.t('templateBuilder.smartProductListDefaultButton'));
      }

      const translationValue = button.querySelector(TRANSLATION_MARKUP);

      if (!translationValue) {
        const { newHtml } = generateTranslations(translator.global.t('templateBuilder.smartProductListDefaultButton'), { newElement: true });
        button.innerHTML = `${newHtml}`;
      } else if (translationValue && translationValue.innerHTML === '') {
        translationValue.innerHTML = translator.global.t('templateBuilder.smartProductListDefaultButton');
      }

      layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL_TRANSLATION}', button.innerHTML);
    } else {
      layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL}', translator.global.t('templateBuilder.smartProductListDefaultButton'));
      const { newHtml } = generateTranslations(translator.global.t('templateBuilder.smartProductListDefaultButton'));
      layout = layout.replaceAll('{PRODUCT_BUTTON_LABEL_TRANSLATION}', `${newHtml}`);
    }

    const listTitle = detached.querySelector('.spm_product_list_title');

    if (listTitle) {
      const titleTranslationValue = listTitle.querySelector(TRANSLATION_MARKUP);

      if (titleTranslationValue && titleTranslationValue.innerHTML === '') {
        titleTranslationValue.innerHTML = translator.global.t('templateBuilder.smartProductListDefaultTitle');
      }

      layout = layout.replace('{PRODUCT_LIST_TITLE}', listTitle.innerHTML);
    } else {
      const { newHtml } = generateTranslations(translator.global.t('templateBuilder.smartProductListDefaultTitle'));
      layout = layout.replace('{PRODUCT_LIST_TITLE}', `${newHtml}`);
    }

    const attributesToKeep = ['engine', 'ids', 'excluded_ids', 'excluded_categories', 'excluded_manufacturers', 'nb_weeks'];
    attributesToKeep.forEach((value) => {
      const pattern = new RegExp(`s-${value}="([^"]*)"`, 'is');
      const find = previousLayout?.match(pattern);
      if (find) {
        layout = layout.replace(pattern, `s-${value}="${find[1]}"`);
      }
    });
    addSmartListOriginalCode(widgetId, layout);
  }
  if (previousLayout && property.value === 'custom') {
    let layout = previousLayout;
    layout = layout.replace(/spm_product_list_(horizontal|vertical)_\d/g, 'spm_product_list_custom');
    addSmartListOriginalCode(widgetId, layout);
  }
};
